/**
 * Time helpers for the time clock: every time is shown in the RESTAURANT's time zone
 * (restaurants.timezone, from /api/timeclock/v2/me), never the browser's.
 */

export function fmtTime(iso, tz, opts = {}) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined, ...opts });
}

export function fmtDate(iso, tz, opts = {}) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz || undefined, ...opts });
}

export function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

export function fmtHours(hours) {
  return `${(Number(hours) || 0).toFixed(2)} h`;
}

/** The device's local time WITH its offset (an offline punch carries when it happened). */
export function isoWithOffset(date = new Date()) {
  const pad = (n) => String(Math.abs(Math.trunc(n))).padStart(2, '0');
  const off = -date.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:` +
    `${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${pad(off / 60)}:${pad(off % 60)}`;
}

function zoneOffsetMs(utcMs, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs)).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour % 24, +parts.minute, +parts.second);
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/** 'YYYY-MM-DDTHH:mm' on the restaurant's wall clock -> an ISO time in UTC. */
export function zonedInputToIso(value, tz) {
  if (!value) return null;
  const [d, t] = value.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = (t || '00:00').split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  let utc = guess - zoneOffsetMs(guess, tz);
  utc = guess - zoneOffsetMs(utc, tz); // DST edge: the offset at the answer, not at the guess
  return new Date(utc).toISOString();
}

/** An ISO time -> 'YYYY-MM-DDTHH:mm' on the restaurant's wall clock (for <input type=datetime-local>). */
export function isoToZonedInput(iso, tz) {
  if (!iso) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(new Date(iso)).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${String(+parts.hour % 24).padStart(2, '0')}:${parts.minute}`;
}

/** Today's date (YYYY-MM-DD) in the restaurant's zone. */
export function zonedToday(tz, offsetDays = 0) {
  const d = new Date(Date.now() + offsetDays * 86400000);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}
